import { useState } from 'react';
import { formatTime } from '../game/state.ts';
import { LEVELS, LEVEL_INFO, type Level } from '../game/types.ts';
import type { SavedGame } from '../storage/storage.ts';
import { HelpIcon, SettingsIcon, StatsIcon } from './Icons.tsx';

interface HomeProps {
  saved: Partial<Record<Level, SavedGame>>;
  loading: Level | null;
  error: string | null;
  onPlay: (level: Level, fresh: boolean) => void;
  onOpenSettings: () => void;
  onOpenStats: () => void;
  onOpenHelp: () => void;
}

/** Filled cells of a saved game, for the progress bar. */
const progress = (g: SavedGame) => {
  const givens = g.puzzle.clues;
  const filled = Array.from(g.values).filter((c) => c !== '0').length;
  return Math.round(((filled - givens) / (81 - givens)) * 100);
};

export function Home({ saved, loading, error, onPlay, onOpenSettings, onOpenStats, onOpenHelp }: HomeProps) {
  const [confirming, setConfirming] = useState<Level | null>(null);

  return (
    <div className="home">
      <header className="home-head">
        <h1 className="wordmark">Sudoku</h1>
        <div className="head-actions">
          <button type="button" className="icon-btn" onClick={onOpenHelp} aria-label="How to play" title="How to play">
            <HelpIcon />
          </button>
          <button type="button" className="icon-btn" onClick={onOpenStats} aria-label="Stats" title="Stats">
            <StatsIcon />
          </button>
          <button type="button" className="icon-btn" onClick={onOpenSettings} aria-label="Settings" title="Settings">
            <SettingsIcon />
          </button>
        </div>
      </header>
      <p className="home-intro">Choose a level. Each one is set by the hardest technique the puzzle needs.</p>

      {error ? (
        <p className="home-error" role="alert">
          {error}
        </p>
      ) : null}

      <ol className="levels">
        {LEVELS.map((level, i) => {
          const info = LEVEL_INFO[level];
          const game = saved[level];
          const isLoading = loading === level;
          return (
            <li key={level} className="level-row" data-level={level}>
              <div className="level-pips" aria-hidden="true">
                {LEVELS.map((_, p) => (
                  <i key={p} className={p <= i ? 'on' : ''} />
                ))}
              </div>
              <div className="level-text">
                <h2>{info.name}</h2>
                <p>{info.blurb}</p>
                {game ? (
                  <p className="level-saved">
                    <span className="meter" aria-hidden="true">
                      <span style={{ width: `${Math.max(progress(game), 2)}%` }} />
                    </span>
                    {progress(game) > 0 ? `${progress(game)}% filled` : 'Started'}, {formatTime(game.elapsedMs)} played
                  </p>
                ) : null}
              </div>
              <div className="level-actions">
                {confirming === level ? (
                  <>
                    <span className="confirm-text">Replace your saved game?</span>
                    <button
                      type="button"
                      className="btn small danger"
                      onClick={() => {
                        setConfirming(null);
                        onPlay(level, true);
                      }}
                    >
                      Replace
                    </button>
                    <button type="button" className="btn small" onClick={() => setConfirming(null)}>
                      Keep
                    </button>
                  </>
                ) : game ? (
                  <>
                    <button type="button" className="btn primary" onClick={() => onPlay(level, false)} disabled={!!loading}>
                      Resume
                    </button>
                    <button type="button" className="btn" onClick={() => setConfirming(level)} disabled={!!loading}>
                      New game
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => onPlay(level, true)}
                    disabled={!!loading}
                    aria-busy={isLoading}
                  >
                    {isLoading ? 'Loading…' : 'Play'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
